import { FunctionComponent, ReactElement, useCallback, useState } from "react";

import {
    FormControl,
    FormHelperText,
    Icon,
    InputAdornment,
    InputLabel,
    MenuItem,
    ListItemText as MuiListItemText,
    Select,
    TextField,
    Tooltip,
    Typography,
    Button,
    styled,
} from "@mui/material";

import { LoadingButton } from "@mui/lab";

import { Add, ArrowDropDown, InfoRounded } from "@mui/icons-material";

import * as Yup from "yup";
import { useFormik } from "formik";
import lodash from "lodash";
import { useNavigate, useParams } from "react-router-dom";

import {
    CircularLoader,
    IUploadAssetsFormValues,
    Paper,
    UploadAssetsDialog,
    WorkspaceToolbar,
} from "../../components/common";
import { ImageCarousel } from "../../components/common";
import {
    useCreateProductVariant,
    useGetProduct,
    useUpdateProductVariant,
    useGetPackagingRules,
    useCreatePackagingRule,
    useCreateReel,
    useDeleteReel,
    useCopyAssets,
    useCopyBlocks,
} from "../../hooks";
import { useGetBlocks } from "../../hooks";
import {
    useCreateAssets,
    useMessage,
    useToggleCallbacks,
} from "../../sdk/hooks";
import { CopyToVariantDialog } from "./CopyToVariantDialog";
import { IProductVariant } from "../../types";
import { ProductStatus, productStatusByEnum } from "../../utils/enums";

import { InventorySection } from "./InventorySection";
import { ContentBlocksSection } from "./ContentBlocksSection";
import { VariantContainer } from "./VariantContainer";

const Root = styled("div")`
    padding: ${({ theme }) => theme.spacing(1, 3)};
    min-height: 86vh;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.5)};

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
        padding: ${theme.spacing(0, 2)};
    }
    
  `};
`;

const Section = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
      flex-direction:column;
      gap: ${theme.spacing(3)};
    }
    
  `}
`;

const SectionTitle = styled(Typography)`
    font-size: 18px;
    font-weight: 500;
    color: #626262;

    margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const SelectField = styled(Select)`
    height: 40px;
`;

const StyledJsonTextField = styled(TextField)`
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: 14px;
    line-height: 1.5;

    padding: ${({ theme }) => theme.spacing(1)};
    background-color: #f5f5f5;
`;

const RightContainer = styled("section")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
    flex: 1;
    max-width: 800px;

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
      max-width: unset;
    }
    
  `}
`;

const LeftContainer = styled("div")``;

const TextFieldWrapper = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(4)};
`;

const ListItemText = styled(MuiListItemText)`
    text-transform: capitalize;
`;

const Row = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};

    width: 100%;

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
          flex-direction: column;
    }
  `}
`;

const TextFieldContainer = styled("div")`
    height: 40px;
    width: 100%;
`;

const StyledArrowDown = styled(ArrowDropDown)`
    margin-right: ${({ theme }) => theme.spacing(3)};
`;

const MediaHeader = styled("div")`
    display: flex;
    justify-content: space-between;
`;

const MediaContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2.5)};

    border: 1px solid #d9d9d9;
    border-radius: ${({ theme }) => theme.spacing(1)};

    padding: ${({ theme }) => theme.spacing(2.5)};
`;

const ImagesContainer = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const PackagingRuleContainer = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => theme.spacing(1)};
    border: 1px solid #e0e0e0;
    border-radius: ${({ theme }) => theme.spacing(1)};
    margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const PackagingRuleField = styled(TextField)`
    flex: 1;
`;

const ReelGrid = styled("div")`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: ${({ theme }) => theme.spacing(2)};

    margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const ReelCard = styled("div")`
    display: flex;
    flex-direction: column;

    border: 1px solid #e0e0e0;
    border-radius: ${({ theme }) => theme.spacing(1)};
    overflow: hidden;
`;

const ReelThumbnail = styled("div")`
    width: 100%;
    aspect-ratio: 9 / 16;
    background-color: #f5f5f5;

    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

const ReelCardBody = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.5)};

    padding: ${({ theme }) => theme.spacing(1)};
`;

const ReelItem = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};

    padding: ${({ theme }) => theme.spacing(1)};
    margin-bottom: ${({ theme }) => theme.spacing(1)};

    border: 1px solid #e0e0e0;
    border-radius: ${({ theme }) => theme.spacing(1)};
  
`;

const ReelField = styled(TextField)`
    flex: 1;
`;

const validationSchema = Yup.object({
    title: Yup.string().min(1).required("Please enter a valid variant name"),
    shopifyVariantId: Yup.string()
        .min(1)
        .required("Please enter a valid shopify variant identifier"),
    price: Yup.number()
        .integer()
        .required("Please enter a selling price for the variant"),
    shippingPrice: Yup.number()
        .integer()
        .required("Please enter a shipping price for the variant"),
    mrp: Yup.number().integer().required("Please enter a MRP for the variant"),
    sku: Yup.string().required(),
    position: Yup.number().integer().required(),
    status: Yup.number()
        .integer()
        .oneOf([
            ProductStatus.ARCHIVED,
            ProductStatus.DRAFT,
            ProductStatus.PUBLISHED,
        ])
        .required(),
    meta: Yup.string()
        .required("Metadata is required")
        .test("is-json", "Invalid JSON format", (value) => {
            if (!value) return false;
            try {
                JSON.parse(value);
                return true;
            } catch (error) {
                return false;
            }
        }),
    amazonRedirectUrl: Yup.string().url("Please enter a valid URL").nullable(),
    flipkartRedirectUrl: Yup.string().url("Please enter a valid URL").nullable(),
});

const statuses = Object.keys(productStatusByEnum);

export interface IProductVariantSchema extends Omit<IProductVariant, "meta"> {
    meta: string;
    amazonRedirectUrl?: string | null;
    flipkartRedirectUrl?: string | null;
}

export const EditProductVariantScreen: FunctionComponent = (): ReactElement => {
    const params = useParams();
    const newProduct = params.variantId === "new";
    const navigate = useNavigate();
    const message = useMessage();

    const [selectedVariant, setSelectedVariant] =
        useState<IProductVariantSchema>({
            id: "",
            title: "",
            shopifyVariantId: "",
            price: 0,
            shippingPrice: 0,
            mrp: 0,
            sku: "",
            productId: "",
            position: 0,
            status: ProductStatus.DRAFT,
            meta: "{}",
            amazonRedirectUrl: null,
            flipkartRedirectUrl: null,
        });
    const [initialValues, setInitialValues] = useState<IProductVariantSchema>({
        id: "",
        title: "",
        shopifyVariantId: "",
        price: 0,
        shippingPrice: 0,
        mrp: 0,
        sku: "",
        productId: "",
        position: 0,
        status: ProductStatus.DRAFT,
        meta: "{}",
        amazonRedirectUrl: null,
        flipkartRedirectUrl: null,
    });

    const [newPackagingRule, setNewPackagingRule] = useState({
        maxUnits: '',
        material: '',
        units: '',
    });

    const [newReel, setNewReel] = useState({ url: '', position: '', thumbnailUrl: '' });
    const [openReelThumbnailDialog, setOpenReelThumbnailDialog] = useState(false);
    const [handleReelThumbnailDialog, handleCloseReelThumbnailDialog] =
        useToggleCallbacks(setOpenReelThumbnailDialog);

    const reelThumbnailMutation = useCreateAssets(["products"]);

    const createMutation = useCreateProductVariant("products");
    const updateMutation = useUpdateProductVariant("products");

    const packagingRulesQuery = useGetPackagingRules(
        ["packaging-rules", params.variantId ?? ""],
        params.variantId ?? "",
        params.productId ?? "",
        { enabled: !newProduct }
    );

    const createPackagingRuleMutation = useCreatePackagingRule(["packaging-rules", params.variantId ?? ""]);

    const createReelMutation = useCreateReel(["products"]);
    const deleteReelMutation = useDeleteReel(["products"]);

    const copyAssetsMutation = useCopyAssets(["products"]);
    const copyBlocksMutation = useCopyBlocks(["content-blocks", params.variantId ?? ""]);

    const blocksQuery = useGetBlocks(
        ["content-blocks", params.variantId ?? ""],
        params.productId ?? "",
        params.variantId ?? "",
        { enabled: !newProduct && Boolean(params.variantId) }
    );

    const [openCopyAssetsDialog, setOpenCopyAssetsDialog] = useState(false);
    const [openCopyBlocksDialog, setOpenCopyBlocksDialog] = useState(false);

    const {
        values,
        handleChange,
        handleSubmit,
        errors,
        touched,
        handleBlur,
        dirty,
        isValid,
    } = useFormik<IProductVariantSchema>({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: (values0) => {
            if (newProduct) {
                createMutation.mutate(
                    {
                        title: values0.title,
                        productId: params?.productId ?? "",
                        shopifyVariantId: values0.shopifyVariantId,
                        price: values0.price * 100,
                        mrp: values0.mrp * 100,
                        shippingPrice: values0.shippingPrice * 100,
                        sku: values0.sku,
                        position: values0.position,
                        status: values0.status,
                        meta: JSON.parse(values0.meta),
                        amazonRedirectUrl: values0.amazonRedirectUrl || null,
                        flipkartRedirectUrl: values0.flipkartRedirectUrl || null,
                    },
                    {
                        onSuccess: (product) => {
                            const createdVariant = lodash.differenceWith(
                                product.variants,
                                getQuery.data?.variants ?? [],
                                (variant1, variant2) =>
                                    variant1.id === variant2.id,
                            );

                            if (createdVariant.length === 1) {
                                navigate(
                                    `/products/${params?.productId}/variants/${createdVariant[0].id}/edit`,
                                    { replace: true },
                                );
                                message.showSuccess(
                                    "Created product variant successfully",
                                );
                            } else {
                                message.showError(
                                    "Something went wrong. More than one variant was created.",
                                );
                            }
                        },
                        onError: (error) => {
                            message.showError(error);
                        },
                    },
                );
            } else {
                updateMutation.mutate(
                    {
                        id: params.variantId ?? "",
                        title: values0.title,
                        productId: params?.productId ?? "",
                        shopifyVariantId: values0.shopifyVariantId,
                        price: values0.price * 100,
                        mrp: values0.mrp * 100,
                        shippingPrice: values0.shippingPrice * 100,
                        sku: values0.sku,
                        position: values0.position,
                        status: values0.status,
                        meta: JSON.parse(values0.meta),
                        amazonRedirectUrl: values0.amazonRedirectUrl || null,
                        flipkartRedirectUrl: values0.flipkartRedirectUrl || null,
                    },
                    {
                        onSuccess: () => {
                            message.showSuccess(
                                "Updated product variant successfully",
                            );
                        },
                        onError: (error) => {
                            message.showError(error);
                        },
                    },
                );
            }
        },
    });

    const getQuery = useGetProduct(["products"], params.productId ?? "", {
        enabled: params.productId !== undefined && params.productId !== "new",
        onSuccess: (product) => {
            const variant = product.variants.find(
                (variant) => variant.id === params.variantId,
            );
            if (!variant) {
                if (!newProduct) {
                    message.showError("Selected variant not found");
                }
            } else {
                setSelectedVariant({
                    ...variant,
                    meta: JSON.stringify(variant.meta),
                });
                setInitialValues({
                    id: variant.id,
                    title: variant.title,
                    shopifyVariantId: variant.shopifyVariantId,
                    price: variant.price,
                    shippingPrice: variant.shippingPrice,
                    mrp: variant.mrp,
                    sku: variant.sku,
                    productId: variant.productId,
                    position: variant.position,
                    status: variant.status,
                    meta: JSON.stringify(variant.meta ?? {}),
                    amazonRedirectUrl: variant.amazonRedirectUrl ?? null,
                    flipkartRedirectUrl: variant.flipkartRedirectUrl ?? null,
                });
            }

            if (newProduct) {
                setInitialValues((values) => ({
                    ...values,
                    position: product.variants.length + 1,
                }));
            }
        },
    });

    const variantImages = getQuery.data?.variants
        .find((variant) => variant.id === params.variantId)
        ?.images?.slice()
        .sort(
            (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
        );

    const assetIds = variantImages?.map((variant) => variant.assetId);

    const [openAssetDialog, setOpenAssetDialog] = useState(false);
    const [handleAssetDialog, handleCloseAssetDialog] =
        useToggleCallbacks(setOpenAssetDialog);

    const assetsMutation = useCreateAssets(["products"]);

    const handleFileSave = useCallback(
        (formValues: IUploadAssetsFormValues) => {
            if (formValues.files.length > 0) {
                assetsMutation.mutate(
                    {
                        files: formValues.files,
                        tags: formValues.tags ?? undefined,
                        position: formValues.position,
                        header: formValues.header,
                        description: formValues.description,
                        altText: formValues.altText,
                        productId: params.productId ?? "",
                        variantId: params.variantId ?? "",
                    },
                    {
                        onError: () => {
                            message.showError(
                                "Failed to upload files. Please try again.",
                            );
                        },
                        onSuccess: () => {
                            message.showSuccess("Files uploaded successfully!");
                            handleCloseAssetDialog();
                        },
                    },
                );
            } else {
                message.showError("Please select files to upload.");
            }
        },
        [
            assetsMutation,
            params.productId,
            params.variantId,
            message,
            handleCloseAssetDialog,
        ],
    );

    const handleSave = useCallback(() => handleSubmit(), [handleSubmit]);

    const handleMaxUnitsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setNewPackagingRule(previousRule => ({ 
            ...previousRule, 
            maxUnits: event.target.value
        }));
    }, []);

    const handleMaterialChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setNewPackagingRule(previousRule => ({ 
            ...previousRule, 
            material: event.target.value
        }));
    }, []);

    const handleUnitsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setNewPackagingRule(previousRule => ({ 
            ...previousRule, 
            units: event.target.value
        }));
    }, []);

    const handleCreatePackagingRule = useCallback(() => {
        if (!params.variantId) return;
        if (!params.productId) return;
        if (newProduct) return;
        
        createPackagingRuleMutation.mutate(
            {
                variantId: params.variantId,
                productId: params.productId,
                maxUnits: parseInt(newPackagingRule.maxUnits) || 1,
                material: parseInt(newPackagingRule.material) || 1,
                units: parseInt(newPackagingRule.units) || 1,
            },
            {
                onSuccess: () => {
                    message.showSuccess("Packaging rule created successfully");
                    setNewPackagingRule({ maxUnits: '', material: '', units: '' });
                },
                onError: (error) => {
                    message.showError(error);
                },
            }
        );
    }, [createPackagingRuleMutation, params.variantId, params.productId, newProduct, newPackagingRule, message]);

    const handleReelThumbnailSave = useCallback(
        (formValues: IUploadAssetsFormValues) => {
            if (formValues.files.length > 0) {
                reelThumbnailMutation.mutate(
                    {
                        files: formValues.files,
                        tags: "reel-thumbnail",
                        position: 1,
                        productId: params.productId ?? "",
                        variantId: params.variantId ?? "",
                    },
                    {
                        onSuccess: (assets) => {
                            if (assets[0]) {
                                const thumbnailUrl = `${process.env.REACT_APP_CLOUDFRONT_URL}/${assets[0].objectKey.replace(/\.(png|jpg|jpeg)$/i, ".webp")}`;
                                setNewReel((r) => ({ ...r, thumbnailUrl }));
                            }
                            handleCloseReelThumbnailDialog();
                        },
                        onError: () => {
                            message.showError("Failed to upload thumbnail. Please try again.");
                        },
                    },
                );
            } else {
                message.showError("Please select a file to upload.");
            }
        },
        [reelThumbnailMutation, params.productId, params.variantId, message, handleCloseReelThumbnailDialog],
    );

    const handleCreateReel = useCallback(() => {
        if (!params.variantId || !params.productId || newProduct) return;

        createReelMutation.mutate(
            {
                variantId: params.variantId,
                productId: params.productId,
                url: newReel.url,
                thumbnailUrl: newReel.thumbnailUrl || undefined,
                position: parseInt(newReel.position) || 0,
            },
            {
                onSuccess: () => {
                    message.showSuccess("Reel added successfully");
                    setNewReel({ url: '', position: '', thumbnailUrl: '' });
                },
                onError: (error) => {
                    message.showError(error);
                },
            }
        );
    }, [createReelMutation, params.variantId, params.productId, newProduct, newReel, message]);

    const handleDeleteReel = useCallback((reelId: string) => {
        if (!params.variantId || !params.productId) return;

        deleteReelMutation.mutate(
            {
                reelId,
                variantId: params.variantId,
                productId: params.productId,
            },
            {
                onSuccess: () => {
                    message.showSuccess("Reel deleted successfully");
                },
                onError: (error) => {
                    message.showError(error);
                },
            }
        );
    }, [deleteReelMutation, params.variantId, params.productId, message]);

    const handleCopyAssets = useCallback((targetProductId: string, targetVariantId: string) => {
        if (!params.variantId || !params.productId) return;

        copyAssetsMutation.mutate(
            {
                srcProductId: params.productId,
                srcVariantId: params.variantId,
                targetProductId,
                targetVariantId,
            },
            {
                onSuccess: (result) => {
                    message.showSuccess(`Copied ${result.images} image(s) and ${result.reels} reel(s) successfully`);
                    setOpenCopyAssetsDialog(false);
                },
                onError: (error) => {
                    message.showError(error);
                },
            }
        );
    }, [copyAssetsMutation, params.productId, params.variantId, message]);

    const handleCopyBlocks = useCallback((targetProductId: string, targetVariantId: string) => {
        if (!params.variantId || !params.productId) return;

        copyBlocksMutation.mutate(
            {
                srcProductId: params.productId,
                srcVariantId: params.variantId,
                targetProductId,
                targetVariantId,
            },
            {
                onSuccess: (result) => {
                    message.showSuccess(`Copied ${result.blocks} block(s) successfully`);
                    setOpenCopyBlocksDialog(false);
                },
                onError: (error) => {
                    message.showError(error);
                },
            }
        );
    }, [copyBlocksMutation, params.productId, params.variantId, message]);

    if (getQuery.isLoading) {
        return <CircularLoader />;
    }

    return (
        <>
            <WorkspaceToolbar
                title={
                    newProduct
                        ? "Create Product Variant"
                        : "Edit Product Variant"
                }
                enableSearch={false}
                breadcrumbs={[
                    { title: "Products", link: "/products" },
                    {
                        title: getQuery.data?.title ?? "",
                        link: `/products/${params.productId}`,
                    },
                    {
                        title: newProduct
                            ? "Create Product Variant"
                            : selectedVariant?.title ?? "",
                        link: `/products/${params.productId}`,
                    },
                ]}
                actions={
                    <>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            size="small"
                            endIcon={<Icon>check_circle</Icon>}
                            onClick={handleSave}
                            disabled={!dirty || !isValid}
                            loading={
                                createMutation.isLoading ||
                                updateMutation.isLoading
                            }>
                            Save
                        </LoadingButton>
                    </>
                }
            />
            <Root>
                <Section>
                    <LeftContainer>
                        <VariantContainer
                            productId={params.productId ?? ""}
                            currentVariantId={params.variantId ?? ""}
                            variants={getQuery.data?.variants ?? []}
                        />
                    </LeftContainer>
                    <RightContainer>
                        <MediaContainer>
                            <MediaHeader>
                                <SectionTitle>Media</SectionTitle>
                                <div style={{ display: "flex", gap: 8 }}>
                                    {!newProduct && ((variantImages?.length ?? 0) > 0 || (getQuery.data?.variants.find((v) => v.id === params.variantId)?.reels?.length ?? 0) > 0) && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            style={{ height: "36px" }}
                                            onClick={() => setOpenCopyAssetsDialog(true)}>
                                            Copy To
                                        </Button>
                                    )}
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        style={{ height: "36px" }}
                                        endIcon={<Add />}
                                        onClick={handleAssetDialog}>
                                        ADD
                                    </LoadingButton>
                                </div>
                            </MediaHeader>
                            <ImagesContainer>
                                <ImageCarousel
                                    imageUrls={variantImages ?? []}
                                    assetIds={assetIds ?? []}
                                />
                            </ImagesContainer>
                        </MediaContainer>

                        {openCopyAssetsDialog && (
                            <CopyToVariantDialog
                                open={openCopyAssetsDialog}
                                title="Copy Assets To Variant"
                                onClose={() => setOpenCopyAssetsDialog(false)}
                                onConfirm={handleCopyAssets}
                                isLoading={copyAssetsMutation.isLoading}
                            />
                        )}
                        <Paper>
                            <SectionTitle>General</SectionTitle>

                            {openAssetDialog && (
                                <UploadAssetsDialog
                                    open={openAssetDialog}
                                    onClose={handleCloseAssetDialog}
                                    onFileSave={handleFileSave}
                                />
                            )}

                            <TextFieldWrapper>
                                <Row>
                                    <TextFieldContainer>
                                        <TextField
                                            required={true}
                                            label="Title"
                                            fullWidth={true}
                                            name="title"
                                            value={values.title}
                                            onChange={handleChange}
                                            size="small"
                                            variant="outlined"
                                            onBlur={handleBlur}
                                            error={
                                                Boolean(errors.title) &&
                                                touched.title
                                            }
                                            helperText={
                                                touched.title && errors.title
                                            }
                                            placeholder="Indian Oud"
                                        />
                                    </TextFieldContainer>
                                </Row>
                                <Row>
                                    <TextFieldContainer>
                                        <TextField
                                            required={true}
                                            label="SKU"
                                            fullWidth={true}
                                            name="sku"
                                            value={values.sku}
                                            onChange={handleChange}
                                            size="small"
                                            variant="outlined"
                                            onBlur={handleBlur}
                                            error={
                                                Boolean(errors.sku) &&
                                                touched.sku
                                            }
                                            helperText={
                                                touched.sku && errors.sku
                                            }
                                        />
                                    </TextFieldContainer>
                                </Row>
                                <Row>
                                    <TextFieldContainer>
                                        <TextField
                                            required={true}
                                            label="Shopify Variant Identifier"
                                            fullWidth={true}
                                            name="shopifyVariantId"
                                            value={values.shopifyVariantId}
                                            onChange={handleChange}
                                            size="small"
                                            variant="outlined"
                                            onBlur={handleBlur}
                                            error={
                                                Boolean(
                                                    errors.shopifyVariantId,
                                                ) && touched.shopifyVariantId
                                            }
                                            helperText={
                                                touched.shopifyVariantId &&
                                                errors.shopifyVariantId
                                            }
                                        />
                                    </TextFieldContainer>
                                    <TextFieldContainer />
                                </Row>
                                <Row>
                                    <TextFieldContainer>
                                        <FormControl
                                            size="small"
                                            fullWidth={true}
                                            required={true}>
                                            <InputLabel>Status</InputLabel>
                                            <SelectField
                                                label="Status"
                                                name="status"
                                                size="small"
                                                fullWidth={true}
                                                value={values.status}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                IconComponent={(props) => (
                                                    <StyledArrowDown
                                                        {...props}
                                                    />
                                                )}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <Tooltip
                                                            enterTouchDelay={0}
                                                            placement="top"
                                                            title={
                                                                <>
                                                                    Status of
                                                                    the product
                                                                    variant.
                                                                    Only
                                                                    published
                                                                    product
                                                                    variants are
                                                                    visible on
                                                                    the website.
                                                                </>
                                                            }>
                                                            <InfoRounded
                                                                htmlColor="#626262"
                                                                fontSize="small"
                                                            />
                                                        </Tooltip>
                                                    </InputAdornment>
                                                }
                                                error={
                                                    Boolean(errors.status) &&
                                                    touched.status
                                                }>
                                                {statuses.map((status) => (
                                                    <MenuItem
                                                        key={status}
                                                        value={status}>
                                                        <ListItemText
                                                            primary={
                                                                productStatusByEnum[
                                                                    Number(
                                                                        status,
                                                                    )
                                                                ]
                                                            }
                                                        />
                                                    </MenuItem>
                                                ))}
                                            </SelectField>
                                            {Boolean(errors.status) &&
                                                touched.status && (
                                                    <FormHelperText>
                                                        {errors.status}
                                                    </FormHelperText>
                                                )}
                                        </FormControl>
                                    </TextFieldContainer>
                                    <TextFieldContainer />
                                </Row>
                                <Row>
                                    <TextFieldContainer>
                                        <TextField
                                            label="Amazon Redirect URL"
                                            fullWidth={true}
                                            name="amazonRedirectUrl"
                                            value={values.amazonRedirectUrl ?? ""}
                                            onChange={handleChange}
                                            size="small"
                                            variant="outlined"
                                            onBlur={handleBlur}
                                            error={
                                                Boolean(errors.amazonRedirectUrl) &&
                                                touched.amazonRedirectUrl
                                            }
                                            helperText={
                                                touched.amazonRedirectUrl &&
                                                errors.amazonRedirectUrl
                                            }
                                            placeholder="https://www.amazon.in/dp/..."
                                        />
                                    </TextFieldContainer>
                                    <TextFieldContainer>
                                        <TextField
                                            label="Flipkart Redirect URL"
                                            fullWidth={true}
                                            name="flipkartRedirectUrl"
                                            value={values.flipkartRedirectUrl ?? ""}
                                            onChange={handleChange}
                                            size="small"
                                            variant="outlined"
                                            onBlur={handleBlur}
                                            error={
                                                Boolean(errors.flipkartRedirectUrl) &&
                                                touched.flipkartRedirectUrl
                                            }
                                            helperText={
                                                touched.flipkartRedirectUrl &&
                                                errors.flipkartRedirectUrl
                                            }
                                            placeholder="https://www.flipkart.com/product/p/..."
                                        />
                                    </TextFieldContainer>
                                </Row>
                            </TextFieldWrapper>
                        </Paper>

                        {/* Pricing  */}
                        <Paper>
                            <SectionTitle>Pricing</SectionTitle>
                            <TextFieldWrapper>
                                <Row>
                                    <TextFieldContainer>
                                        <TextField
                                            required={true}
                                            label="Price"
                                            fullWidth={true}
                                            name="price"
                                            value={values.price}
                                            onChange={handleChange}
                                            size="small"
                                            variant="outlined"
                                            onBlur={handleBlur}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        ₹
                                                    </InputAdornment>
                                                ),
                                            }}
                                            error={
                                                Boolean(errors.price) &&
                                                touched.price
                                            }
                                            helperText={
                                                touched.price && errors.price
                                            }
                                        />
                                    </TextFieldContainer>
                                    <TextFieldContainer>
                                        <TextField
                                            required={true}
                                            label="MRP"
                                            fullWidth={true}
                                            name="mrp"
                                            value={values.mrp}
                                            onChange={handleChange}
                                            size="small"
                                            variant="outlined"
                                            onBlur={handleBlur}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        ₹
                                                    </InputAdornment>
                                                ),
                                            }}
                                            error={
                                                Boolean(errors.mrp) &&
                                                touched.mrp
                                            }
                                            helperText={
                                                touched.mrp && errors.mrp
                                            }
                                        />
                                    </TextFieldContainer>
                                </Row>
                                <Row>
                                    <TextFieldContainer>
                                        <TextField
                                            required={true}
                                            label="Shipping Price"
                                            fullWidth={true}
                                            name="shippingPrice"
                                            value={values.shippingPrice}
                                            onChange={handleChange}
                                            size="small"
                                            variant="outlined"
                                            onBlur={handleBlur}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        ₹
                                                    </InputAdornment>
                                                ),
                                            }}
                                            error={
                                                Boolean(errors.shippingPrice) &&
                                                touched.shippingPrice
                                            }
                                            helperText={
                                                touched.shippingPrice &&
                                                errors.shippingPrice
                                            }
                                        />
                                    </TextFieldContainer>
                                    <TextFieldContainer />
                                </Row>
                            </TextFieldWrapper>
                        </Paper>
                        {/* Metadata */}
                        <Paper>
                            <SectionTitle>Misc</SectionTitle>
                            <TextFieldWrapper>
                                <StyledJsonTextField
                                    required={true}
                                    label="Metadata"
                                    multiline={true}
                                    rows={12}
                                    fullWidth={true}
                                    name="meta"
                                    value={values.meta}
                                    onChange={handleChange}
                                    size="small"
                                    variant="outlined"
                                    onBlur={handleBlur}
                                    error={Boolean(errors.meta) && touched.meta}
                                    helperText={touched.meta && errors.meta}
                                />
                            </TextFieldWrapper>
                        </Paper>
                        
                        {!newProduct && (
                            <Paper>
                                <SectionTitle>Reels</SectionTitle>

                                <ReelGrid>
                                    {getQuery.data?.variants
                                        .find((v) => v.id === params.variantId)
                                        ?.reels
                                        ?.slice()
                                        .sort((a, b) => a.position - b.position)
                                        .map((reel) => (
                                            <ReelCard key={reel.id}>
                                                <ReelThumbnail>
                                                    {reel.thumbnailUrl ? (
                                                        <img
                                                            src={reel.thumbnailUrl}
                                                            alt="thumbnail"
                                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                        />
                                                    ) : (
                                                        <Icon style={{ fontSize: 40, color: "#bdbdbd" }}>videocam</Icon>
                                                    )}
                                                </ReelThumbnail>
                                                <ReelCardBody>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                        style={{ wordBreak: "break-all", lineHeight: 1.3 }}>
                                                        {reel.url}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Position: {reel.position}
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        fullWidth
                                                        onClick={() => handleDeleteReel(reel.id)}
                                                        disabled={deleteReelMutation.isLoading}>
                                                        Delete
                                                    </Button>
                                                </ReelCardBody>
                                            </ReelCard>
                                        ))}
                                </ReelGrid>

                                <ReelItem>
                                    <ReelField
                                        label="URL"
                                        size="small"
                                        value={newReel.url}
                                        onChange={(e) =>
                                            setNewReel((r) => ({ ...r, url: e.target.value }))
                                        }
                                        placeholder="https://www.instagram.com/reel/..."
                                    />
                                    <ReelField
                                        label="Position"
                                        size="small"
                                        type="number"
                                        value={newReel.position}
                                        onChange={(e) =>
                                            setNewReel((r) => ({ ...r, position: e.target.value }))
                                        }
                                        style={{ maxWidth: 100 }}
                                    />
                                    {newReel.thumbnailUrl ? (
                                        <img
                                            src={newReel.thumbnailUrl}
                                            alt="thumbnail"
                                            style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
                                            onClick={handleReelThumbnailDialog}
                                        />
                                    ) : (
                                        <LoadingButton
                                            variant="outlined"
                                            size="small"
                                            onClick={handleReelThumbnailDialog}
                                            loading={reelThumbnailMutation.isLoading}>
                                            Thumbnail
                                        </LoadingButton>
                                    )}
                                    <LoadingButton
                                        variant="contained"
                                        size="small"
                                        onClick={handleCreateReel}
                                        loading={createReelMutation.isLoading}
                                        disabled={!newReel.url}>
                                        Add
                                    </LoadingButton>
                                </ReelItem>

                                {openReelThumbnailDialog && (
                                    <UploadAssetsDialog
                                        open={openReelThumbnailDialog}
                                        onClose={handleCloseReelThumbnailDialog}
                                        onFileSave={handleReelThumbnailSave}
                                    />
                                )}
                            </Paper>
                        )}

                        {!newProduct && (
                            <InventorySection
                                variantId={params.variantId ?? ""}
                            />
                        )}

                        {!newProduct && (
                            <Paper>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                                    <SectionTitle style={{ marginBottom: 0 }}>Content Blocks</SectionTitle>
                                    {(blocksQuery.data?.length ?? 0) > 0 && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setOpenCopyBlocksDialog(true)}>
                                            Copy To
                                        </Button>
                                    )}
                                </div>
                                <ContentBlocksSection
                                    productId={params.productId ?? ""}
                                    variantId={params.variantId ?? ""}
                                />
                            </Paper>
                        )}

                        {openCopyBlocksDialog && (
                            <CopyToVariantDialog
                                open={openCopyBlocksDialog}
                                title="Copy Content Blocks To Variant"
                                onClose={() => setOpenCopyBlocksDialog(false)}
                                onConfirm={handleCopyBlocks}
                                isLoading={copyBlocksMutation.isLoading}
                            />
                        )}

                        {!newProduct && (
                            <Paper>
                                <SectionTitle>Packaging Rule</SectionTitle>
                                
                                {packagingRulesQuery.isLoading && (
                                    <Typography variant="body2" color="textSecondary">
                                        Loading packaging rule...
                                    </Typography>
                                )}
                                
                                {!packagingRulesQuery.isLoading && packagingRulesQuery.data && (() => {
                                    const existingRule = packagingRulesQuery.data.find(rule => rule.variantId === params.variantId);
                                    
                                    if (existingRule) {
                                        return (
                                            <PackagingRuleContainer>
                                                <PackagingRuleField
                                                    label="Max Units"
                                                    type="text"
                                                    size="small"
                                                    value={existingRule.maxUnits}
                                                    disabled
                                                />
                                                <PackagingRuleField
                                                    label="Material"
                                                    type="text"
                                                    size="small"
                                                    value={existingRule.material}
                                                    disabled
                                                />
                                                <PackagingRuleField
                                                    label="Units"
                                                    type="text"
                                                    size="small"
                                                    value={existingRule.units}
                                                    disabled
                                                />
                                            </PackagingRuleContainer>
                                        );
                                    }
                                    
                                    return (
                                        <PackagingRuleContainer>
                                            <PackagingRuleField
                                                label="Max Units"
                                                type="text"
                                                size="small"
                                                value={newPackagingRule.maxUnits}
                                                onChange={handleMaxUnitsChange}
                                            />
                                            <PackagingRuleField
                                                label="Material"
                                                type="text"
                                                size="small"
                                                value={newPackagingRule.material}
                                                onChange={handleMaterialChange}
                                            />
                                            <PackagingRuleField
                                                label="Units"
                                                type="text"
                                                size="small"
                                                value={newPackagingRule.units}
                                                onChange={handleUnitsChange}
                                            />
                                            <LoadingButton
                                                variant="contained"
                                                size="small"
                                                onClick={handleCreatePackagingRule}
                                                loading={createPackagingRuleMutation.isLoading}
                                            >
                                                Create
                                            </LoadingButton>
                                        </PackagingRuleContainer>
                                    );
                                })()}
                            </Paper>
                        )}
                    </RightContainer>
                </Section>
            </Root>
        </>
    );
};
